<!-- BaseButton.vue - Unified Button Component for Modals -->
<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click')"
  >
    <!-- Loading spinner -->
    <component
      v-if="loading"
      :is="LoaderIcon"
      class="animate-spin -ml-1 mr-3 h-5 w-5"
      :class="iconColorClass"
    />

    <!-- Icon -->
    <component
      v-else-if="icon"
      :is="icon"
      :class="[iconSize, iconColorClass]"
      :style="iconOnly ? {} : { marginRight: '0.75rem' }"
    />

    <!-- Text content -->
    <span v-if="!iconOnly">
      {{ loading ? loadingText : text }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

const LoaderIcon = Loader2

export interface Props {
  text?: string
  loadingText?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  icon?: any
  iconOnly?: boolean
  fullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  loadingText: 'Loading...',
  type: 'button',
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  icon: undefined,
  iconOnly: false,
  fullWidth: false
})

defineEmits<{
  click: []
}>()

// Computed classes
const buttonClasses = computed(() => {
  const baseClasses = [
    'inline-flex',
    'justify-center',
    'items-center',
    'font-medium',
    'rounded-[2rem]',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'transform',
    'hover:scale-105',
    'active:scale-95'
  ]

  // Size classes
  const sizeClasses = {
    sm: ['px-3', 'py-1.5', 'text-sm'],
    md: ['px-4', 'py-2', 'text-base'],
    lg: ['px-6', 'py-3', 'text-base'],
    xl: ['px-8', 'py-4', 'text-lg']
  }

  // Variant classes
  const variantClasses = {
    primary: [
      'text-white',
      'bg-primary',
      'hover:bg-primary-hover',
      'active:bg-primary-active',
      'focus:ring-primary/50',
      'shadow-md',
      'hover:shadow-lg'
    ],
    secondary: [
      'text-gray-700',
      'dark:text-gray-200',
      'bg-white',
      'dark:bg-gray-600',
      'border',
      'border-gray-300',
      'dark:border-gray-500',
      'hover:bg-gray-50',
      'dark:hover:bg-gray-500',
      'focus:ring-gray-500',
      'shadow-sm'
    ],
    danger: [
      'text-white',
      'bg-danger',
      'hover:bg-danger-hover',
      'active:bg-danger-active',
      'focus:ring-danger/50',
      'shadow-md',
      'hover:shadow-lg'
    ],
    success: [
      'text-white',
      'bg-success',
      'hover:bg-success-hover',
      'active:bg-success-active',
      'focus:ring-success/50',
      'shadow-md',
      'hover:shadow-lg'
    ],
    warning: [
      'text-white',
      'bg-warning',
      'hover:bg-warning-hover',
      'active:bg-warning-active',
      'focus:ring-warning/50',
      'shadow-md',
      'hover:shadow-lg'
    ],
    ghost: [
      'text-gray-700',
      'dark:text-gray-200',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500'
    ],
    outline: [
      'text-primary',
      'dark:text-primary-dark-text',
      'border-2',
      'border-primary',
      'dark:border-primary-dark',
      'hover:bg-primary-bg',
      'dark:hover:bg-primary-dark-bg',
      'focus:ring-primary/50'
    ]
  }

  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []

  // Icon-only classes
  const iconOnlyClasses = props.iconOnly ? ['aspect-square'] : []

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...widthClasses,
    ...iconOnlyClasses
  ]
})

const iconSize = computed(() => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6'
  }
  return sizes[props.size]
})

const iconColorClass = computed(() => {
  if (props.variant === 'secondary' || props.variant === 'ghost') {
    return 'text-gray-500 dark:text-gray-400'
  }
  if (props.variant === 'outline') {
    return 'text-primary dark:text-primary-dark-text'
  }
  return 'text-white'
})
</script>

<style scoped>
/* Base transition for smooth hover effects */
button {
  transition: all 0.2s ease-in-out;
}
</style>
